import React, { useCallback, useContext, useState } from 'react';
import { MultiValueProps } from 'react-select/lib/components/MultiValue';
import { useElementRef } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { PillBase } from '@tanium/react-pill';
import { Stack } from '@tanium/react-stack';
import { ThemeDefinition } from '@tanium/react-theme-definition';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';
import { MultiCollapsibleContext } from './createValueContainer';
import { useTranslation } from './localization';
import { RemainingPillCount } from './themeDefinitions';
import { SelectSize } from './types';

type Props = MultiValueProps<unknown>;

const StyledIconButton = styled(IconButton)({ display: 'block' });

const RemoveButtonContainer = styled('span')(
  css({
    paddingLeft: ThemeSpace.minor[1],
  }),
);

const DEFAULT_MAX_MAX_WIDTH = 200;
const minMaxWidth = 50;

const TruncatedText = styled('div', {
  shouldForwardProp: (p) => p !== 'textColor' && p !== 'innerRef',
})<{ textColor?: ThemeDefinition }>(({ textColor: color }) =>
  css({
    color,
    fontWeight: 'bold',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

export const createMultiValuePill =
  (size: SelectSize, enableMaxWidth: boolean) => (props: Props) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const value = props.selectProps.getOptionLabel!(props.data);
    const { getValue } = props;
    const allValues = getValue() as unknown[];
    const currentPillIndex = allValues.findIndex((item) => item === props.data);
    const maxMaxWidth = enableMaxWidth ? DEFAULT_MAX_MAX_WIDTH : Infinity;
    const [maxWidth, setMaxWidth] = useState(maxMaxWidth);
    const [element, setElement] = useState<HTMLElement | null>(null);
    const { element: textContainerElement, ref: textContainerRef } = useElementRef<HTMLElement>();
    const { element: textValueElement, ref: textValueRef } = useElementRef<HTMLElement>();
    const [shouldForceTooltip, setShouldForceTooltip] = useState(false);
    const removeButtonSizePx = size === 'lg' ? 14 : 12;
    const { t } = useTranslation();

    const { maxPills = allValues.length } = useContext(MultiCollapsibleContext);

    const showPill = currentPillIndex < maxPills;
    const showCollapsedPills = currentPillIndex + 1 === maxPills && allValues.length > maxPills;
    // BasePill supports a maxWidth for ellipsizing its text, but Select elements are not width bound
    // as they are defined by us, so we need to be able to dynamically set the maxWidth of our pills
    const updateMaxWidth = useCallback(
      (parent: Element | null) => {
        if (!element) {
          return;
        }
        if (!parent) {
          setMaxWidth(maxMaxWidth);
          return;
        }
        const elementComputedStyle = getComputedStyle(element);
        const textContainerWidth = textContainerElement?.offsetWidth ?? 0;
        const removeButtonWidth = removeButtonSizePx ?? 0;
        const textValueWidth = textValueElement?.getBoundingClientRect().width ?? 0;
        const nonContentWidth = Math.max(
          element.offsetWidth - textContainerWidth + removeButtonWidth,
          0,
        );
        const usableParentWidth =
          parent.clientWidth -
          parseFloat(elementComputedStyle.paddingLeft) -
          parseFloat(elementComputedStyle.paddingRight);
        const newMaxWidth = Math.min(
          Math.max(usableParentWidth - nonContentWidth, minMaxWidth),
          maxMaxWidth,
        );
        setMaxWidth(newMaxWidth);
        setShouldForceTooltip(newMaxWidth > usableParentWidth || textValueWidth > newMaxWidth);
      },
      [
        element,
        textContainerElement?.offsetWidth,
        removeButtonSizePx,
        textValueElement,
        maxMaxWidth,
      ],
    );

    const parentElementRef = useResizeObserver(
      useCallback(
        (e) => {
          updateMaxWidth(e.target);
        },
        [updateMaxWidth],
      ),
    );

    const elementRef = useCallback(
      (node: HTMLElement | null) => {
        setElement(node);
        parentElementRef(node?.offsetParent || null);
      },
      [parentElementRef],
    );

    const remainingCount = allValues.length - maxPills;

    return showPill ? (
      <Stack innerRef={elementRef} m="2px" itemSpacing={1 / 2}>
        <PillBase
          tooltipContent={value}
          maxWidth={maxWidth}
          alwaysEnableTooltip={shouldForceTooltip}
          mainContentRef={textContainerRef}
          size={size}
          trailingContent={
            <RemoveButtonContainer>
              <StyledIconButton
                icon={CloseIcon}
                height={removeButtonSizePx}
                width={removeButtonSizePx}
                label={t('Remove')}
                // removeProps includes an onMouseDown handler that prevents the containing menu from
                // opening/closing when the user removes an item. Use caution when refactoring.
                {...props.removeProps}
              />
            </RemoveButtonContainer>
          }
        >
          <TruncatedText innerRef={textValueRef}>{value}</TruncatedText>
        </PillBase>
        {showCollapsedPills && (
          <PillBase
            alwaysEnableTooltip
            tooltipContent={t(
              '{{count, INTEGER}} more selected item not shown. Click to expand and view.',
              {
                count: remainingCount,
              },
            )}
            size={size}
          >
            <TruncatedText textColor={RemainingPillCount}>
              {t('{{count, INTEGER}} more', { count: remainingCount })}
            </TruncatedText>
          </PillBase>
        )}
      </Stack>
    ) : null;
  };
